import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingLayout from './OnboardingLayout';
import { Chip } from '@/components/atoms';
import { useProfileState } from '@/contexts/ProfileContext';
import { PIVOT_TYPES_ENTERING, PIVOT_TYPES_INSUST, HOURS_PER_WEEK, URGENCY, SITUATIONS } from '@/lib/constants';

export default function Step2Pivot() {
  const navigate = useNavigate();
  const { draft, patchDraft, savedProfile } = useProfileState();
  const [pivotType, setPivotType] = useState('');
  const [hours, setHours] = useState('');
  const [urgency, setUrgency] = useState('');
  const [situation, setSituation] = useState('');

  useEffect(() => {
    const src = { ...(savedProfile || {}), ...draft };
    setPivotType(src.pivot_type || '');
    setHours(src.hours_per_week || '');
    setUrgency(src.urgency || '');
    setSituation(src.current_situation || '');
  }, [draft, savedProfile]);

  const entering = PIVOT_TYPES_ENTERING.map(p => p.id).includes(pivotType);

  const next = () => {
    if (!pivotType) { alert('Pick a pivot type.'); return; }
    if (!hours || !urgency || !situation) { alert('Pick your hours, urgency and situation.'); return; }
    patchDraft({
      pivot_type: pivotType,
      hours_per_week: hours,
      urgency,
      current_situation: situation,
    });
    navigate('/onboarding/review');
  };

  return (
    <OnboardingLayout step={2}>
      <p className="text-[12px] uppercase tracking-[0.14em] text-sd-mid-gray font-medium">Step 3 of 4</p>
      <h2 className="text-[36px] font-semibold text-sd-black mt-2 mb-3 heading-tight">What kind of move are you making?</h2>
      <p className="text-sd-mid-gray text-[15px] mb-8 max-w-[600px] leading-relaxed">
        The most important input — this drives which analysis lens we apply to your career.
      </p>

      {/* Entering Sustainability */}
      <h3 className="text-[14px] font-semibold text-sd-black mb-3">Entering Sustainability</h3>
      <div className="grid md:grid-cols-3 gap-3 mb-7">
        {PIVOT_TYPES_ENTERING.map(p => (
          <PivotCard key={p.id} {...p} selected={pivotType === p.id} onClick={() => setPivotType(p.id)} testId={`pivot-${p.id}`} />
        ))}
      </div>

      {entering && (
        <div className="rounded-card border-l-4 border-sd-warning bg-[#FBF1DE] text-[#7A5B16] p-4 mb-7 text-[13px] leading-relaxed animate-fade-in">
          <strong className="font-semibold">You’re entering sustainability.</strong> We’ll focus on translating your existing skills into sustainability terms, surface roles where your background is an asset (not a liability), and avoid recommending “entry-level” sustainability courses you don’t need.
        </div>
      )}

      <h3 className="text-[14px] font-semibold text-sd-black mb-3">Already in Sustainability</h3>
      <div className="grid md:grid-cols-2 gap-3 mb-10">
        {PIVOT_TYPES_INSUST.map(p => (
          <PivotCard key={p.id} {...p} selected={pivotType === p.id} onClick={() => setPivotType(p.id)} testId={`pivot-${p.id}`} />
        ))}
      </div>

      <div className="mb-7">
        <h3 className="text-[14px] font-semibold text-sd-black mb-2">Hours per week you can invest</h3>
        <div className="flex flex-wrap gap-2">
          {HOURS_PER_WEEK.map(h => (
            <Chip key={h} on={hours === h} onClick={() => setHours(h)} testId={`hours-${h}`}>{h}</Chip>
          ))}
        </div>
      </div>

      <div className="mb-7">
        <h3 className="text-[14px] font-semibold text-sd-black mb-2">Urgency</h3>
        <div className="flex flex-wrap gap-2">
          {URGENCY.map(u => (
            <Chip key={u} on={urgency === u} onClick={() => setUrgency(u)} testId={`urgency-${u}`}>{u}</Chip>
          ))}
        </div>
      </div>

      <div className="mb-10">
        <h3 className="text-[14px] font-semibold text-sd-black mb-2">Current situation</h3>
        <div className="flex flex-wrap gap-2">
          {SITUATIONS.map(s => (
            <Chip key={s} on={situation === s} onClick={() => setSituation(s)} testId={`situation-${s}`}>{s}</Chip>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button className="sd-btn-secondary" onClick={() => navigate('/onboarding/confirm')} data-testid="pivot-back">← Back</button>
        <button className="sd-btn-primary px-8" onClick={next} data-testid="pivot-next">Continue →</button>
      </div>
    </OnboardingLayout>
  );
}

function PivotCard({ title, desc, selected, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left p-4 rounded-card transition-all duration-200 ease-sd
        ${selected
          ? 'bg-sd-black text-white shadow-card-deep'
          : 'sd-card sd-card-hover hover:translate-y-[-2px]'}
      `}
    >
      <div className={`text-[15px] font-semibold mb-1.5 ${selected ? '' : 'text-sd-black'}`}>{title}</div>
      <div className={`text-[13px] leading-snug ${selected ? 'text-[#A8B0A8]' : 'text-sd-mid-gray'}`}>{desc}</div>
    </button>
  );
}
